import { useEffect, useRef, useState, type RefObject } from "react";
import type { Status } from "@delegate/shared";
import { ActivityIndicator } from "@/components/brand/activity-indicator.tsx";
import { DelegateMark } from "@/components/brand/delegate-mark.tsx";
import { Button } from "@/components/ui/button.tsx";
import { takeAvatarAudio } from "./avatar-audio.ts";

type Avatar = {
  context?: AudioContext;
  setFraming(mode: string): void;
  setAngle(angle: number): void;
  setIdle(enabled: boolean): void;
  setExpression(name: string, amount?: number): void;
  attachAudio(stream: MediaStream): Promise<void>;
  detachAudio(): void;
  handleRealtimeEvent(event: Record<string, unknown>): void;
  controls: {
    enabled: boolean;
    enableZoom: boolean;
    enableRotate: boolean;
    enablePan: boolean;
  };
  scene: {
    background: {
      setRGB(r: number, g: number, b: number, space: string): void;
    };
  };
  dispose(): void;
};

/** Renderer only. The parent owns voice; resizing never starts another session. */
export function RepresentativePortrait({
  stream,
  status,
  onReady,
  eventSink,
}: {
  stream: MediaStream | null;
  status: Status;
  onReady: () => void;
  eventSink: RefObject<((event: Record<string, unknown>) => void) | null>;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const avatar = useRef<Avatar | null>(null);
  const ready = useRef(onReady);
  ready.current = onReady;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!canvas.current) return;
    const element = canvas.current;
    let cancelled = false;
    let instance: Avatar | undefined;
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const applyMotion = () => instance?.setIdle(!motion.matches);
    setFailed(false);
    setLoaded(false);
    void (async () => {
      try {
        const sdkURL = "/api/representative/avatar/sdk.mjs";
        const sdk = await import(/* @vite-ignore */ sdkURL);
        if (cancelled) return;
        instance = await sdk.mountAvatar({
          canvas: element,
          manifestURL: "/api/representative/avatar/manifest.json",
        });
        if (!instance) throw new Error("Avatar unavailable");
        if (cancelled) {
          instance.dispose();
          return;
        }
        avatar.current = instance;
        // Renderer consumes motion cues only: it owns no data channel or tool handler.
        eventSink.current = (event) => instance?.handleRealtimeEvent(event);
        instance.context = takeAvatarAudio();
        instance.setFraming("portrait");
        instance.setAngle(0);
        instance.controls.enabled =
          instance.controls.enableZoom =
          instance.controls.enableRotate =
          instance.controls.enablePan =
            false;
        element.style.touchAction = "pan-y";
        const pixel = document.createElement("canvas");
        pixel.width = pixel.height = 1;
        const context = pixel.getContext("2d");
        if (context && element.parentElement) {
          context.fillStyle = getComputedStyle(
            element.parentElement,
          ).backgroundColor;
          context.fillRect(0, 0, 1, 1);
          const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
          instance.scene.background.setRGB(r / 255, g / 255, b / 255, "srgb");
        }
        applyMotion();
        motion.addEventListener("change", applyMotion);
        instance.setExpression("happy", 0.3);
        setLoaded(true);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) ready.current();
      }
    })();
    return () => {
      cancelled = true;
      motion.removeEventListener("change", applyMotion);
      instance?.dispose();
      avatar.current = null;
      eventSink.current = null;
    };
  }, [attempt, eventSink]);

  useEffect(() => {
    if (!loaded || !avatar.current) return;
    const instance = avatar.current;
    if (stream) void instance.attachAudio(stream).catch(() => {});
    else instance.detachAudio();
    return () => instance.detachAudio();
  }, [stream, loaded]);

  useEffect(() => {
    avatar.current?.setExpression(
      status === "ERROR"
        ? "concerned"
        : status === "SPEAKING"
          ? "happy"
          : "listening",
      0.25,
    );
  }, [status, loaded]);

  return (
    <div
      className="relative size-full bg-background"
      data-testid="representative-portrait"
    >
      <canvas
        ref={canvas}
        className="block size-full"
        aria-label="Destiny, Delegate representative, fixed portrait"
      />
      {!loaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
          {failed ? <DelegateMark /> : <ActivityIndicator label="Loading Destiny portrait..." />}
          <p role="status" className="text-xs text-muted-foreground">
            {failed
              ? "Portrait unavailable. You can still talk or use Settings."
              : "Meeting Destiny..."}
          </p>
          {failed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Reload portrait
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
